import { useNavigate } from 'react-router-dom';
import { Virtuoso } from 'react-virtuoso';
import { ArrowRightSmall, ChatFill, LikeFill } from '@peacecover/icon';
import { useGetSceneSubscriptions } from '../../../domains/scene/ui/hooks/useGetSceneSubscriptions';
import EmptyCase from '../../../components/errors/EmptyCase';
import PreviewHeaderItem from './PreviewHeaderItem';
import ThreadContentItem from './ThreadContentItem';
import PreviewCastRelationship from './PreviewCastRelationship';
import { ReactComponent as ThreadIcon } from '../../../icons/thread.svg';
import type { components } from '../../../types/schema';

type SceneSubscription = components['schemas']['ApiV1UserSceneSubscription'];

const PreviewSubscriptionList = () => {
  const { data, fetchNextPage, hasNextPage, isFetching, isLoading } = useGetSceneSubscriptions();
  const previewList = data?.pages?.flatMap((page) => page.userSceneSubscriptions ?? []);

  const loadMore = () => {
    if (isFetching || !hasNextPage) return;
    fetchNextPage();
  };

  if (isLoading) {
    return null;
  }

  if (data?.pages?.length && previewList?.length === 0) {
    return (
      // @ts-ignore
      <EmptyCase emoji="😳" title="보관함이 비어있어요" />
    );
  }

  return (
    <div className="h-full">
      <Virtuoso
        overscan={480}
        defaultItemHeight={260}
        style={{ height: '100%' }}
        data={previewList}
        endReached={loadMore}
        atBottomThreshold={200}
        itemContent={(_index, subscription: SceneSubscription) => (
          <PreviewSubscriptionItem subscription={subscription} />
        )}
      />
    </div>
  );
};

export default PreviewSubscriptionList;

const PreviewSubscriptionItem = ({ subscription }: { subscription: SceneSubscription }) => {
  const navigate = useNavigate();
  const tags = [...(subscription?.keywords ?? []), ...(subscription?.worldview ?? [])];
  
  return (
    <div className="pt-[20px]">
      <div className="px-[22px] flex items-center justify-between">
        <PreviewCastRelationship castRelationship={subscription?.castRelationship ?? null} />
        <div className="flex items-center gap-[4px]">
          <ThreadIcon width={14} height={14} />
          <p className="text-[12px] font-medium leading-[14px] text-pure-white">
            타래 {subscription?.currentSeq}/{subscription?.count}
          </p>
        </div>
      </div>
      <PreviewHeaderItem
        tags={tags}
        summary={subscription?.summary ?? ''}
        className="px-[22px] mt-[16px]"
      />
      <ThreadContentItem
        speaker={subscription?.lastReadContent?.speaker ?? undefined}
        text={subscription?.lastReadContent?.text ?? ''}
        className="mt-[8px]"
        hasLine={false}
      />
      <div className="border-t-[1px] border-b-[5px] border-[#171717] flex items-center justify-between px-[22px] py-[14px]">
        <div className="flex items-center gap-[12px]">
          <div className="flex items-center gap-[4px]">
            <LikeFill size={18} colors={['#FF45A9', '#FF6A6A']} />
            <p className="text-[11px] font-semibold leading-[13px] text-pure-white">
              {subscription?.likesCount}
            </p>
          </div>
          <div className="flex items-center gap-[4px] text-pure-white">
            <ChatFill size={18} />
            <p className="text-[11px] font-semibold leading-[13px]">
              {subscription?.commentsCount}
            </p>
          </div>
        </div>
        <button
          type="button"
          className="text-pure-white text-[12px] font-medium leading-[14px] flex items-center gap-[4px]"
          onClick={() =>
            navigate(`/scene/${subscription.sceneId}/preview?cursor=${subscription.contentId}`)
          }
        >
          이어보기
          <ArrowRightSmall size={14} />
        </button>
      </div>
    </div>
  );
};
